import { Component, type ErrorInfo, type ReactNode } from "react";
import { Icon } from "./Icon";

// A phone can sit for days on a page from an older deploy. When it then needs
// a piece it hasn't loaded yet (a screen, the map), that file is gone from the
// server and the import fails. Reloading picks up the current version; any
// other crash shows what went wrong instead of an empty (black) page.
//
// A file asked for while a deploy is still spreading can come back as the app
// page instead (the server's not-found fallback), and the browser then keeps
// that wrong copy as if it were the file, for a year. So before reloading,
// the failed files are fetched again past the browser's copy.

const RELOADED_AT = "km:reloaded-for-update";

/** The error a browser gives when a script from an older deploy no longer exists. */
export function isStaleChunk(err: unknown): boolean {
  const msg = err instanceof Error ? `${err.name} ${err.message}` : String(err);
  return /dynamically imported module|importing a module script failed|error loading dynamically imported|ChunkLoadError|Unable to preload CSS|MIME type/i.test(msg);
}

/** The app files an error names ("…imported module: https://…/assets/x-1.js", "preload CSS for /assets/x.css"). */
export function assetUrlsIn(err: unknown): string[] {
  const msg = err instanceof Error ? err.message : String(err);
  return [...new Set(msg.match(/(?:https?:\/\/[^\s"')]+)?\/assets\/[\w.-]+\.(?:js|css)/g) ?? [])];
}

/**
 * Reload once for the current version, first replacing the browser's saved
 * copy of any file the error names. A second failure within a minute shows
 * the error instead of looping.
 */
export function reloadForNewVersion(err?: unknown): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOADED_AT) ?? 0);
    if (Date.now() - last < 60_000) return false;
    sessionStorage.setItem(RELOADED_AT, String(Date.now()));
  } catch {
    // Storage blocked: reload anyway, a loop is still bounded by the browser.
  }
  const refetch = assetUrlsIn(err).map((u) => fetch(u, { cache: "reload" }).catch(() => undefined));
  // Don't let a hung request hold the page on a broken screen.
  void Promise.race([Promise.all(refetch), new Promise((r) => setTimeout(r, 5000))]).finally(() => window.location.reload());
  return true;
}

export class ErrorBoundary extends Component<{ children: ReactNode; title?: string }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Kinmantle crashed", error, info.componentStack);
    if (isStaleChunk(error)) reloadForNewVersion(error);
  }
  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="crash" role="alert">
        <Icon name="alert" size="2rem" />
        <h2>{isStaleChunk(error) ? "Kinmantle was updated" : (this.props.title ?? "Something went wrong")}</h2>
        <p className="muted">{isStaleChunk(error) ? "Reload to get the new version." : error.message}</p>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>
          <Icon name="refresh" size="1.1rem" /> Reload
        </button>
      </div>
    );
  }
}
